import React, { useMemo, useCallback } from 'react';
import flattenDeep from 'lodash.flattendeep';
import PropTypes from 'prop-types';
import { useRouteMatch, useParams } from 'react-router-dom';
import { replacePathParams } from 'utils/routes';

import * as Styled from './BreadCrumbs.styled';

const BreadCrumbs = React.memo((props) => {
  const { routesList } = props;

  const { path } = useRouteMatch();

  const params = useParams();

  //If the breadcrumb name is not present then get it from the path.
  const getBreadCrumbNameFromPath = useCallback((givenPath, params) => {
    const splitPaths = replacePathParams(givenPath, params)
      .split('/')
      .filter((i) => i !== '');
    return splitPaths[splitPaths.length - 1];
  }, []);

  //Generate bread crumbs recursively based on the routes array.
  const breadCrumbEntries = useMemo(() => {
    const getBreadCrumbEntries = (routeEntries, currentPath) =>
      routeEntries
        .filter((entry) => new RegExp(entry.path, 'i').test(currentPath))
        .map((entry) => {
          if (entry.ignoreForBreadCrumb) {
            return null;
          }
          if (Array.isArray(entry.component)) {
            return getBreadCrumbEntries(entry.component, currentPath);
          }
          //Replace any params.
          const { breadCrumbName, path: routePath } = entry;
          return {
            name:
              breadCrumbName || getBreadCrumbNameFromPath(routePath, params),
            url: replacePathParams(routePath, params),
          };
        })
        .filter((val) => !!val);

    return flattenDeep(getBreadCrumbEntries(routesList, path)).reverse();
  }, [path, params, routesList, getBreadCrumbNameFromPath]);

  return (
    <Styled.Container>
      {breadCrumbEntries &&
        breadCrumbEntries.map(({ name, url }, index) => (
          <Styled.LinkContainer key={`${name}${url}`} to={url}>
            {name}
            {index < breadCrumbEntries.length - 1 && <Styled.ArrowRightIcon />}
          </Styled.LinkContainer>
        ))}
    </Styled.Container>
  );
});

BreadCrumbs.displayName = 'BreadCrumbs';

BreadCrumbs.propTypes = {
  routesList: PropTypes.arrayOf(
    PropTypes.shape({
      breadCrumbName: PropTypes.string,
      path: PropTypes.string.isRequired,
      component: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array,
        PropTypes.func,
      ]),
      ignoreForBreadCrumb: PropTypes.bool,
    })
  ).isRequired,
};

export default BreadCrumbs;
